import type { IServerSideGetRowsRequest } from 'ag-grid-community';
import type { ISearchResponse, ServerSideBlotterSearchRequest } from 'apiConnector/blotters';
import { useServerSideBlotterDataSource } from 'components/shared/Blotter/Grid/Hooks/useServerSideDatasource';
import { useCallback } from 'react';
import type { Observable } from 'rxjs';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';

interface UseBlotterServerSideDatasourceProps<TRequest, TItem, TError> {
  blotterRequest: TRequest;
  columnDefinitions: readonly IBlotterColumnDefinition<TItem>[];
  searchItems: (req: ServerSideBlotterSearchRequest) => Observable<ISearchResponse<TItem>>;
  setLastBlotterRequest: (value: ServerSideBlotterSearchRequest) => void;
  translateRequest: (gridParams: IServerSideGetRowsRequest, request: TRequest) => ServerSideBlotterSearchRequest;
  onItemsReceived?: (response: ISearchResponse<TItem>) => void;
  onItemsRequested?: () => void;
  onRequestFailed?: (error: TError) => void;
}

export function useBlotterServerSideDatasource<TRequest, TItem, TError = Error>({
  blotterRequest,
  searchItems,
  setLastBlotterRequest,
  translateRequest,
  onItemsReceived,
  onItemsRequested,
  onRequestFailed,
}: UseBlotterServerSideDatasourceProps<TRequest, TItem, TError>) {
  const searchServerSideBlotterItems = useCallback(
    (req: ServerSideBlotterSearchRequest) => {
      setLastBlotterRequest(req);
      return searchItems(req);
    },
    [setLastBlotterRequest, searchItems],
  );

  return useServerSideBlotterDataSource<ServerSideBlotterSearchRequest, TRequest, TItem, TError>({
    blotterRequest,
    searchItems: searchServerSideBlotterItems,
    translateRequest,
    onItemsRequested,
    onItemsReceived,
    onRequestFailed,
    isNativePaginationEnabled: true,
  });
}
